// ---------------------------------------------------------------------------
// Popups for sales, pre-sales and administrators: events that pop up once (from notifications) and daily
// cards (briefing, cold deals, passed close dates, deadlines, weekly snapshot).
// ---------------------------------------------------------------------------
import { z } from 'zod';

/** Notification kinds that also pop up once. */
export const POPUP_EVENT_KINDS = [
  'presales.handoff', 'presales.clarification', 'presales.quotation_ready', 'presales.new_document', 'presales.submittal_revise', 'quote.approval', 'quote.decided', 'tool.calibration', 'tool.handover', 'intel.assigned',
  'priority.requested', 'priority.approved', 'priority.rejected', 'priority.high',
  'deal.won', 'project.assigned', 'project.ticket', 'po.approval', 'po.decided',
  'project.priority', 'project.priority_high', 'project.priority_decided', 'vo.approval', 'vo.decided',
  'accounts.invoice_request', 'accounts.po_approved', 'accounts.deal_won', 'accounts.invoice_done', 'accounts.ready_weekly',
  'logistics.alert', 'logistics.handover', 'logistics.handover_done',
  'presales.support', 'presales.support_late', 'presales.support_accepted', 'presales.quotation_sent',
  'task.high',
] as const;

export interface PopupDealRef { id: string; name: string; accountName: string | null }

export interface PopupEvent {
  id: string;
  kind: (typeof POPUP_EVENT_KINDS)[number];
  title: string;
  body: string | null;
  actorName: string | null;
  createdAt: string;
  recordId: string | null;
  objectName: string | null;
  /** The deal as it is now, so actions already done elsewhere aren't offered again. */
  deal: { name: string; presalesStatus: string | null; priorityStatus: string | null; isClosed: boolean; quoteStageId: string | null; inQuoteStage: boolean } | null;
}

export type PopupCard =
  | { key: string; type: 'briefing_sales'; tasksToday: number; tasksOverdue: number; nextActionOverdue: PopupDealRef[]; closingThisWeek: (PopupDealRef & { closeDate: string })[]; clarifications: PopupDealRef[] }
  | { key: string; type: 'briefing_presales'; notStarted: number; highPriority: PopupDealRef[]; deadlines: (PopupDealRef & { deadline: string })[] }
  | { key: string; type: 'cold'; deals: (PopupDealRef & { daysIdle: number | null; health: string | null })[] }
  | { key: string; type: 'close_passed'; deals: (PopupDealRef & { closeDate: string; wonStageId: string | null; lostStageId: string | null })[] }
  | { key: string; type: 'deadline'; deals: (PopupDealRef & { deadline: string; daysLeft: number; presalesStatus: string | null })[] }
  | { key: string; type: 'projects_briefing'; overdue: number; dueSoon: number; tickets: number; priorities: number; items: { projectId: string; projectNumber: string; title: string; reason: string; late: boolean }[] }
  | { key: string; type: 'weekly'; from: string; to: string; rows: { source: string; registered: number; quoted: number; won: number; lost: number; wonValue: number }[] };

export interface PopupsResult { events: PopupEvent[]; cards: PopupCard[] }

export const popupDismissSchema = z.object({ key: z.string().trim().min(1).max(100) }).strict();
export const popupSeenSchema = z.object({ id: z.string().uuid() }).strict();
